import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as endpoints from '../api/endpoints/vault.ts';
import type { CreateVaultItemParams, UpdateVaultItemParams } from '../api/endpoints/vault.ts';
import { STALE_TIMES } from '../lib/constants.ts';

export const vaultQueryKeys = {
  all: ['vault'] as const,
  list: (params?: Record<string, unknown>) => ['vault', 'list', params] as const,
  detail: (id: string) => ['vault', 'detail', id] as const,
};

export function useVaultItems(params?: {
  page?: number;
  pageSize?: number;
  teamId?: string;
  kind?: string;
  search?: string;
}) {
  return useQuery({
    queryKey: vaultQueryKeys.list(params),
    queryFn: () => endpoints.listVaultItems(params),
    staleTime: STALE_TIMES.secrets,
  });
}

export function useCreateVaultItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: CreateVaultItemParams) => endpoints.createVaultItem(params),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: vaultQueryKeys.all });
    },
  });
}

export function useGenerateVaultItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: endpoints.GenerateVaultItemParams) => endpoints.generateVaultItem(params),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: vaultQueryKeys.all });
    },
  });
}

export function useUpdateVaultItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, params }: { id: string; params: UpdateVaultItemParams }) =>
      endpoints.updateVaultItem(id, params),
    onSuccess: (_d, { id }) => {
      void qc.invalidateQueries({ queryKey: vaultQueryKeys.all });
      void qc.invalidateQueries({ queryKey: vaultQueryKeys.detail(id) });
    },
  });
}

export function useDeleteVaultItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => endpoints.deleteVaultItem(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: vaultQueryKeys.all });
    },
  });
}

export function useRevealVaultItem() {
  return useMutation({
    mutationFn: (id: string) => endpoints.revealVaultItem(id),
  });
}

export function useInjectVaultItem() {
  return useMutation({
    mutationFn: ({
      id,
      serverId,
      path,
      mode,
      ttl,
      prefer,
      field,
      verify,
    }: {
      id: string;
      serverId: string;
      path: string;
      mode?: string;
      ttl?: number;
      prefer?: 'sftp' | 'priv_exec' | '';
      field?: string;
      verify?: boolean;
    }) => endpoints.injectVaultItem(id, { serverId, path, mode, ttl, prefer, field, verify }),
  });
}
